import { Layout } from '@/components/Layout';
import { IMAGES } from '@/assets/images';
import { CONTACT_PHONE, CONTACT_EMAIL } from '@/lib/index';
import { Shield, Lock, Award, MapPin, Search, Building2, Phone, Mail } from 'lucide-react';
import { motion } from 'framer-motion';

export default function About() {
  const fadeInUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.7 }
  };

  const values = [
    {
      icon: Shield,
      title: 'Transparency',
      description: 'Every transaction is clear, documented, and legally verified. No hidden fees, no surprises.'
    },
    {
      icon: Lock,
      title: 'Security',
      description: 'Your investment is protected through bank escrow systems and comprehensive legal frameworks.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'From architecture to construction, we deliver premium quality that stands the test of time.'
    }
  ];

  const services = [
    {
      icon: Search,
      title: 'Find Your Property',
      description: 'Access exclusive plots, villas, and condominiums in prime Zanzibar locations. We help you discover the perfect investment opportunity.'
    },
    {
      icon: Building2,
      title: 'Build Your Vision',
      description: 'Complete architecture and construction management. From design to keys, we handle every detail of your project.'
    },
    {
      icon: Lock,
      title: 'Secure Your Investment',
      description: 'The Vault System™ ensures your money is protected in bank escrow until all legal requirements and construction are complete.'
    }
  ];

  return (
    <Layout>
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.HERO_BG_2}
            alt="Zanzibar coastline"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>
        
        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.h1
            className="text-5xl md:text-7xl font-serif font-bold text-foreground mb-6"
            {...fadeInUp}
          >
            About <span className="text-accent">Anchor Vault Estate</span>
          </motion.h1>
          <motion.p
            className="text-xl text-muted-foreground max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            Anchored in Trust. Built for Legacy.
          </motion.p>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-4xl font-serif font-bold text-foreground mb-6">
                Who We Are
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  Anchor Vault Estate is a luxury real estate agency based in Paje, Zanzibar, Tanzania. We specialize in providing secure, transparent real estate investment opportunities across the island's most desirable locations.
                </p>
                <p>
                  Our mission is to revolutionize real estate investment in Tanzania through our proprietary Vault System™ — a guaranteed payment framework that protects your investment from start to finish. We believe that buying property should be exciting, not stressful.
                </p>
                <p>
                  With deep local expertise and international standards, we bridge the gap between vision and reality. Whether you're seeking a beachfront plot, a luxury villa, or a development project, we provide end-to-end support with complete peace of mind.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={IMAGES.VILLA_PROPERTY_1}
                  alt="Luxury property in Zanzibar"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-card">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-4xl font-serif font-bold text-foreground mb-4">
              Our Location
            </h2>
            <div className="flex items-center justify-center gap-2 text-accent mb-6">
              <MapPin className="w-5 h-5" />
              <span className="text-lg font-medium">Paje, Zanzibar, Tanzania</span>
            </div>
          </motion.div>

          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Paje is a stunning coastal village on the southeast coast of Zanzibar, renowned for its pristine white-sand beaches, turquoise waters, and world-class kitesurfing conditions. Once a quiet fishing village, Paje has emerged as one of Zanzibar's most sought-after destinations for both tourism and real estate investment.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              The area offers a perfect blend of natural beauty, modern infrastructure, and investment potential. With growing international interest, strategic beachfront locations, and Tanzania's favorable property laws for foreign investors, Paje represents an exceptional opportunity for those seeking luxury real estate in East Africa's premier island paradise.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-4xl font-serif font-bold text-foreground mb-4">
              What We Do
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From discovery to delivery, we handle every aspect of your real estate journey.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: index * 0.1 }}
                  className="bg-card border border-border rounded-2xl p-8 hover:border-accent/50 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-accent" />
                  </div>
                  <h3 className="text-2xl font-serif font-semibold text-foreground mb-4">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {service.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-card">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-4xl font-serif font-bold text-foreground mb-4">
              Our Values
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The principles that guide every decision we make.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: index * 0.1 }}
                  className="bg-background border border-border rounded-2xl p-8 text-center hover:border-accent/50 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-accent" />
                  </div>
                  <h3 className="text-2xl font-serif font-semibold text-foreground mb-4">
                    {value.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-4xl font-serif font-bold mb-6">
              Ready to Start Your Journey?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Let's discuss your real estate goals and how we can help you achieve them.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <a
                href={`tel:${CONTACT_PHONE}`}
                className="flex items-center gap-3 bg-accent text-accent-foreground px-8 py-4 rounded-xl font-medium hover:bg-accent/90 transition-all duration-300 hover:scale-105"
              >
                <Phone className="w-5 h-5" />
                {CONTACT_PHONE}
              </a>
              
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="flex items-center gap-3 bg-primary-foreground/10 backdrop-blur-sm border border-primary-foreground/20 px-8 py-4 rounded-xl font-medium hover:bg-primary-foreground/20 transition-all duration-300 hover:scale-105"
              >
                <Mail className="w-5 h-5" />
                {CONTACT_EMAIL}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}